import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Info, Home, Video, Users, Blend, Brain, Heart, Sparkles, Activity, Leaf, HandHeart } from 'lucide-react';

// Información de modalidades de tratamiento
export const MODALIDADES_INFO = {
  residencial: {
    nombre: 'Residencial',
    nombreCompleto: 'Tratamiento Residencial',
    icon: Home,
    color: 'blue',
    descripcion: 'El paciente vive en el centro de rehabilitación durante todo el tratamiento. Es la modalidad más intensiva.',
    caracteristicas: [
      'Atención 24/7 con equipo multidisciplinario',
      'Ambiente controlado y libre de sustancias',
      'Duración: 6 a 12 meses según evolución',
      'Ideal para adicciones severas o múltiples recaídas',
      'Incluye desintoxicación supervisada médicamente'
    ],
    indicado: 'Personas con adicción severa, sin red de apoyo familiar, o que necesitan alejarse de su entorno habitual.',
    duracion: '6 a 12 meses según evolución'
  },
  ambulatorio: {
    nombre: 'Ambulatorio',
    nombreCompleto: 'Tratamiento Ambulatorio',
    icon: Users,
    color: 'purple',
    descripcion: 'El paciente asiste a sesiones programadas pero vive en su casa. Permite mantener la vida laboral y familiar.',
    caracteristicas: [
      'Sesiones de terapia individual y grupal',
      'Permite mantener trabajo y vida familiar',
      'Duración: 6 a 12 meses según evolución',
      'Costo generalmente menor que residencial',
      'Requiere compromiso y autodisciplina'
    ],
    indicado: 'Personas con adicción leve a moderada, buena red de apoyo, y capacidad de mantener abstinencia fuera del centro.',
    duracion: '6 a 12 meses según evolución'
  },
  online: {
    nombre: 'Online',
    nombreCompleto: 'Tratamiento Online',
    icon: Video,
    color: 'emerald',
    descripcion: 'Terapia y seguimiento a través de videollamadas y plataformas digitales. Acceso desde cualquier lugar.',
    caracteristicas: [
      'Acceso desde cualquier lugar con internet',
      'Ideal para zonas sin centros especializados',
      'Duración: 6 a 12 meses según evolución',
      'Sesiones individuales y grupos virtuales',
      'Herramientas digitales de seguimiento'
    ],
    indicado: 'Personas que viven lejos de centros, tienen movilidad reducida, o prefieren mayor privacidad.',
    duracion: '6 a 12 meses según evolución'
  },
  mixto: {
    nombre: 'Mixto',
    nombreCompleto: 'Tratamiento Mixto/Híbrido',
    icon: Blend,
    color: 'amber',
    descripcion: 'Combina modalidades residencial, ambulatoria y/o online según la fase del tratamiento.',
    caracteristicas: [
      'Flexibilidad para adaptar el tratamiento',
      'Puede iniciar residencial y continuar ambulatorio',
      'Duración: 6 a 12 meses según evolución',
      'Transición gradual a la vida cotidiana',
      'Seguimiento continuo post-tratamiento'
    ],
    indicado: 'Personas que necesitan una transición gradual o que se benefician de diferentes modalidades en distintas etapas.',
    duracion: '6 a 12 meses según evolución'
  },
  presencial: {
    nombre: 'Presencial',
    nombreCompleto: 'Tratamiento Presencial',
    icon: Users,
    color: 'teal',
    descripcion: 'Sesiones de tratamiento en persona en las instalaciones del centro o consultorio.',
    caracteristicas: [
      'Contacto directo con el terapeuta',
      'Evaluación física y emocional en vivo',
      'Duración: 6 a 12 meses según evolución',
      'Mayor conexión interpersonal',
      'Acceso a instalaciones y recursos físicos'
    ],
    indicado: 'Personas que prefieren el contacto humano directo y tienen acceso geográfico al centro.',
    duracion: '6 a 12 meses según evolución'
  },
  hibrido: {
    nombre: 'Híbrido',
    nombreCompleto: 'Tratamiento Híbrido',
    icon: Blend,
    color: 'amber',
    descripcion: 'Combina sesiones presenciales y online según las necesidades del paciente.',
    caracteristicas: [
      'Flexibilidad de horarios y ubicación',
      'Combina lo mejor de ambas modalidades',
      'Duración: 6 a 12 meses según evolución',
      'Continuidad del tratamiento asegurada',
      'Ideal para transiciones graduales'
    ],
    indicado: 'Personas con horarios variables, que viajan frecuentemente, o prefieren flexibilidad.',
    duracion: '6 a 12 meses según evolución'
  }
};

// Información de enfoques/tipos de tratamiento
export const ENFOQUES_INFO = {
  'cognitivo-conductual': {
    nombre: 'Terapia Cognitivo-Conductual (TCC)',
    nombreCorto: 'Cognitivo-Conductual',
    icon: Brain,
    color: 'blue',
    descripcion: 'Se enfoca en identificar y cambiar patrones de pensamiento y comportamiento negativos.',
    tecnicas: [
      'Identificación de pensamientos automáticos',
      'Reestructuración cognitiva',
      'Técnicas de manejo de ansiedad',
      'Prevención de recaídas',
      'Entrenamiento en habilidades sociales'
    ],
    efectividad: 'Altamente efectiva para adicciones. Respaldada por amplia evidencia científica.',
    duracion: '6 a 12 meses'
  },
  'cognitivo': {
    nombre: 'Terapia Cognitiva',
    nombreCorto: 'Cognitivo',
    icon: Brain,
    color: 'blue',
    descripcion: 'Se centra en modificar los patrones de pensamiento disfuncionales que mantienen la adicción.',
    tecnicas: [
      'Identificación de creencias irracionales',
      'Reestructuración de pensamientos',
      'Desarrollo de pensamiento racional',
      'Análisis de distorsiones cognitivas',
      'Automonitoreo de pensamientos'
    ],
    efectividad: 'Efectiva para cambiar la forma de pensar sobre el consumo y las situaciones de riesgo.',
    duracion: '6 a 12 meses'
  },
  'conductual': {
    nombre: 'Terapia Conductual',
    nombreCorto: 'Conductual',
    icon: Activity,
    color: 'orange',
    descripcion: 'Se enfoca en modificar comportamientos problemáticos a través del aprendizaje.',
    tecnicas: [
      'Manejo de contingencias (recompensas)',
      'Exposición gradual',
      'Desensibilización sistemática',
      'Economía de fichas',
      'Modelado de conductas'
    ],
    efectividad: 'Muy efectiva para cambiar comportamientos específicos relacionados con el consumo.',
    duracion: '6 a 12 meses'
  },
  'humanista': {
    nombre: 'Enfoque Humanista',
    nombreCorto: 'Humanista',
    icon: Heart,
    color: 'pink',
    descripcion: 'Centra el tratamiento en el potencial de crecimiento personal y la autorrealización.',
    tecnicas: [
      'Terapia centrada en la persona',
      'Escucha activa y empatía',
      'Autoaceptación y autoconocimiento',
      'Exploración de valores personales',
      'Desarrollo del potencial humano'
    ],
    efectividad: 'Efectiva para fortalecer la motivación y el sentido de vida.',
    duracion: '6 a 12 meses'
  },
  'holistico': {
    nombre: 'Enfoque Holístico',
    nombreCorto: 'Holístico',
    icon: Sparkles,
    color: 'purple',
    descripcion: 'Trata a la persona de manera integral: cuerpo, mente y espíritu.',
    tecnicas: [
      'Meditación y mindfulness',
      'Yoga y ejercicio físico',
      'Nutrición terapéutica',
      'Terapias complementarias (arte, música)',
      'Conexión con la naturaleza'
    ],
    efectividad: 'Complementa otros tratamientos. Mejora bienestar general y manejo del estrés.',
    duracion: '6 a 12 meses'
  },
  'integrativo': {
    nombre: 'Enfoque Integrativo',
    nombreCorto: 'Integrativo',
    icon: Leaf,
    color: 'green',
    descripcion: 'Combina diferentes técnicas y enfoques según las necesidades del paciente.',
    tecnicas: [
      'Evaluación personalizada',
      'Combinación de TCC, humanista y otros',
      'Adaptación según progreso',
      'Tratamiento individualizado',
      'Flexibilidad terapéutica'
    ],
    efectividad: 'Permite adaptar el tratamiento a cada persona. Enfoque más personalizado.',
    duracion: '6 a 12 meses'
  },
  '12-pasos': {
    nombre: 'Programa de 12 Pasos',
    nombreCorto: '12 Pasos',
    icon: HandHeart,
    color: 'emerald',
    descripcion: 'Modelo de recuperación basado en principios espirituales y apoyo grupal.',
    tecnicas: [
      'Aceptación de la impotencia ante la adicción',
      'Inventario moral personal',
      'Reparación de daños causados',
      'Meditación y oración',
      'Servicio a otros adictos en recuperación'
    ],
    efectividad: 'Altamente efectivo cuando se combina con comunidad de apoyo (AA, NA).',
    duracion: 'Proceso de por vida (seguimiento continuo)'
  },
  'sistemico': {
    nombre: 'Terapia Sistémica/Familiar',
    nombreCorto: 'Sistémico',
    icon: Users,
    color: 'teal',
    descripcion: 'Aborda la adicción considerando el sistema familiar y relacional del paciente.',
    tecnicas: [
      'Terapia familiar conjunta',
      'Análisis de patrones relacionales',
      'Comunicación familiar efectiva',
      'Establecimiento de límites sanos',
      'Codependencia y roles familiares'
    ],
    efectividad: 'Muy efectiva especialmente cuando hay disfunción familiar asociada.',
    duracion: '6 a 12 meses (incluye sesiones familiares)'
  }
};

// Componente de Badge clickeable
export function InfoBadge({ tipo, valor, className = '' }) {
  const [open, setOpen] = useState(false);
  
  const info = tipo === 'modalidad' 
    ? MODALIDADES_INFO[valor?.toLowerCase()] 
    : ENFOQUES_INFO[valor?.toLowerCase()?.replace(/\s+/g, '-')];
  
  if (!info) {
    return <span className={className}>{valor}</span>;
  }
  
  const Icon = info.icon;
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30',
    purple: 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30',
    amber: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
    pink: 'bg-pink-500/20 text-pink-400 hover:bg-pink-500/30',
    orange: 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30',
    teal: 'bg-teal-500/20 text-teal-400 hover:bg-teal-500/30',
    green: 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
  };
  
  // Get short label for display (e.g., "Residencial" instead of "Tratamiento Residencial")
  const shortLabel = tipo === 'modalidad' 
    ? (valor?.charAt(0).toUpperCase() + valor?.slice(1).toLowerCase()) 
    : info.nombreCorto || info.nombre.split('(')[0].trim();
  
  return (
    <>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${colorClasses[info.color]} ${className}`}
      >
        <Icon className="w-3 h-3" />
        {shortLabel}
        <Info className="w-3 h-3 opacity-60" />
      </button>
      
      <InfoDialog 
        open={open} 
        onClose={() => setOpen(false)} 
        info={info}
        tipo={tipo}
      />
    </>
  );
}

// Componente de Dialog informativo
export function InfoDialog({ open, onClose, info, tipo }) {
  if (!info) return null;
  
  const Icon = info.icon;
  const colorClasses = {
    blue: 'text-blue-400 bg-blue-500/20',
    purple: 'text-purple-400 bg-purple-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/20',
    pink: 'text-pink-400 bg-pink-500/20',
    orange: 'text-orange-400 bg-orange-500/20',
    teal: 'text-teal-400 bg-teal-500/20',
    green: 'text-green-400 bg-green-500/20'
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-slate-50">
            <div className={`p-2 rounded-lg ${colorClasses[info.color]}`}>
              <Icon className="w-6 h-6" />
            </div>
            {info.nombre}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-2">
          {/* Descripción principal */}
          <p className="text-slate-300 text-sm leading-relaxed">
            {info.descripcion}
          </p>
          
          {/* Características o Técnicas */}
          <div>
            <h4 className="text-slate-50 font-medium text-sm mb-2">
              {tipo === 'modalidad' ? 'Características:' : 'Técnicas utilizadas:'}
            </h4>
            <ul className="space-y-1.5">
              {(info.caracteristicas || info.tecnicas || []).map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${colorClasses[info.color].split(' ')[1]?.replace('text', 'bg') || 'bg-emerald-400'}`} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Indicado para / Efectividad */}
          {(info.indicado || info.efectividad) && (
            <div className={`p-3 rounded-lg ${colorClasses[info.color].replace('text', 'border').split(' ')[0]} bg-opacity-10`} style={{ backgroundColor: 'rgba(100,100,100,0.1)' }}>
              <h4 className="text-slate-50 font-medium text-sm mb-1">
                {tipo === 'modalidad' ? '¿Para quién es ideal?' : 'Efectividad:'}
              </h4>
              <p className="text-slate-400 text-sm">
                {info.indicado || info.efectividad}
              </p>
            </div>
          )}
          
          {/* Duración */}
          {info.duracion && (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <span className="font-medium text-slate-400">Duración típica:</span>
              {info.duracion}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Componente simple para mostrar tooltip en hover (alternativa)
export function InfoTooltip({ children, info }) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (!info) return children;
  
  return (
    <div 
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 whitespace-nowrap z-50 shadow-lg">
          {info.descripcion}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </div>
      )}
    </div>
  );
}

export default InfoBadge;
